// [DELETION-PIN] Modal saisie du code PIN propriétaire pour valider une suppression
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, X, ShieldAlert } from "lucide-react";

const PIN_LENGTH = 4;

export function DeletionPinDialog({
  open,
  onClose,
  onSubmit,
  title = "Confirmer la suppression",
  description = "Demandez au propriétaire de saisir son code PIN pour valider cette suppression.",
  loading = false,
  error = null,
}) {
  const [digits, setDigits] = useState(() => Array(PIN_LENGTH).fill(""));
  const inputsRef = useRef([]);

  useEffect(() => {
    if (open) {
      setDigits(Array(PIN_LENGTH).fill(""));
      setTimeout(() => inputsRef.current[0]?.focus(), 100);
    }
  }, [open]);

  const setDigit = (i, val) => {
    const v = (val || "").replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
    if (v && i < PIN_LENGTH - 1) inputsRef.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    }
    if (e.key === "Enter" && digits.every((d) => d)) {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, PIN_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const arr = Array(PIN_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) arr[i] = pasted[i];
    setDigits(arr);
    const focusIdx = Math.min(pasted.length, PIN_LENGTH - 1);
    inputsRef.current[focusIdx]?.focus();
  };

  const handleSubmit = () => {
    const pin = digits.join("");
    if (pin.length !== PIN_LENGTH || loading) return;
    onSubmit(pin);
  };

  const filled = digits.every((d) => d);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="deletion-pin-overlay"
      >
        <motion.div
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 300 }}
          className="w-full sm:max-w-md bg-card border border-border rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="deletion-pin-dialog"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-red-500/10 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold text-[16px] leading-tight">
                  {title}
                </h3>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5 font-medium">
                  Autorisation requise
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              data-testid="deletion-pin-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-[13px] text-muted-foreground mb-5 leading-relaxed">
            {description}
          </p>

          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onFocus={(e) => e.target.select()}
                className={`w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-semibold rounded-2xl bg-secondary border-2 outline-none transition-colors ${
                  error ? "border-red-500" : d ? "border-foreground/50" : "border-border"
                } focus:border-foreground/70 text-foreground`}
                data-testid={`deletion-pin-digit-${i}`}
              />
            ))}
          </div>

          {error && (
            <p className="text-red-500 text-[12px] text-center mt-2" data-testid="deletion-pin-error">
              {error}
            </p>
          )}

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 h-12 rounded-2xl bg-secondary text-foreground font-medium text-[14px] hover:bg-muted transition-colors active:scale-[0.98]"
              data-testid="deletion-pin-cancel"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={!filled || loading}
              className="flex-1 h-12 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-semibold text-[14px] disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              data-testid="deletion-pin-submit"
            >
              <Lock className="w-4 h-4" />
              {loading ? "Vérification..." : "Confirmer"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
